'use client';

import { useEffect, useRef } from 'react';

export function LoginBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    const setSize = () => {
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
    };
    setSize();

    type P = { x: number; y: number; v: number; o: number };
    let ps: P[] = [];
    let raf = 0;

    const make = (): P => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      v: Math.random() * 0.25 + 0.05,
      o: Math.random() * 0.35 + 0.15,
    });

    const init = () => {
      ps = [];
      const count = Math.floor(
        (canvas.width * canvas.height) / 12000,
      );
      for (let i = 0; i < count; i++) ps.push(make());
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ps.forEach((p) => {
        p.y -= p.v;
        if (p.y < 0) {
          p.x = Math.random() * canvas.width;
          p.y = canvas.height + Math.random() * 40;
          p.v = Math.random() * 0.25 + 0.05;
          p.o = Math.random() * 0.35 + 0.15;
        }
        ctx.fillStyle = `rgba(128,128,128,${p.o})`;
        ctx.fillRect(p.x, p.y, 0.7, 2.2);
      });
      raf = requestAnimationFrame(draw);
    };

    const observer = new ResizeObserver(() => {
      setSize();
      init();
    });
    observer.observe(parent);

    init();
    raf = requestAnimationFrame(draw);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <style>{`
        .login-accent-lines{position:absolute;inset:0;pointer-events:none;opacity:.5}
        .login-hline,.login-vline{position:absolute;background:hsl(var(--border));will-change:transform,opacity}
        .login-hline{left:0;right:0;height:1px;transform:scaleX(0);transform-origin:50% 50%;animation:loginDrawX .8s cubic-bezier(.22,.61,.36,1) forwards}
        .login-vline{top:0;bottom:0;width:1px;transform:scaleY(0);transform-origin:50% 0%;animation:loginDrawY .9s cubic-bezier(.22,.61,.36,1) forwards}
        .login-hline:nth-child(1){top:20%;animation-delay:.12s}
        .login-hline:nth-child(2){top:50%;animation-delay:.22s}
        .login-hline:nth-child(3){top:80%;animation-delay:.32s}
        .login-vline:nth-child(4){left:25%;animation-delay:.42s}
        .login-vline:nth-child(5){left:50%;animation-delay:.54s}
        .login-vline:nth-child(6){left:75%;animation-delay:.66s}
        .login-hline::after,.login-vline::after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(128,128,128,.2),transparent);opacity:0;animation:loginShimmer .9s ease-out forwards}
        .login-hline:nth-child(1)::after{animation-delay:.12s}
        .login-hline:nth-child(2)::after{animation-delay:.22s}
        .login-hline:nth-child(3)::after{animation-delay:.32s}
        .login-vline:nth-child(4)::after{animation-delay:.42s}
        .login-vline:nth-child(5)::after{animation-delay:.54s}
        .login-vline:nth-child(6)::after{animation-delay:.66s}
        @keyframes loginDrawX{0%{transform:scaleX(0);opacity:0}60%{opacity:.95}100%{transform:scaleX(1);opacity:.7}}
        @keyframes loginDrawY{0%{transform:scaleY(0);opacity:0}60%{opacity:.95}100%{transform:scaleY(1);opacity:.7}}
        @keyframes loginShimmer{0%{opacity:0}35%{opacity:.25}100%{opacity:0}}
      `}</style>

      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none [background:radial-gradient(80%_60%_at_50%_30%,rgba(255,255,255,0.04),transparent_60%)]" />

      {/* Accent lines */}
      <div className="login-accent-lines">
        <div className="login-hline" />
        <div className="login-hline" />
        <div className="login-hline" />
        <div className="login-vline" />
        <div className="login-vline" />
        <div className="login-vline" />
      </div>

      {/* Particles */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full opacity-40 mix-blend-screen pointer-events-none"
      />
    </>
  );
}
